/**
 * Where the device signing key's module is loaded from, so the launch entry does not carry it
 * (`check-first-screen-chunks.mjs`, `DeviceSigningKey`): its code reaches the signed-record and
 * machine-grant vocabulary of `@featit/shared`, about 20 KB the first screen does not draw.
 *
 * - To act on a key (prepare one at sign-in, bind one to a passkey sign-in), await
 *   {@link loadDeviceSigningKey}.
 * - To end the keys a page holds (sign-out), ask {@link peekDeviceSigningKey}: it is undefined until
 *   the module has loaded, and a module that never loaded holds no key, so there is nothing to end.
 *   Sign-out stays synchronous in the tick it is called (`signOut.ts`).
 */
type DeviceSigningKeyModule = typeof import('./deviceSigningKey');

let loaded: DeviceSigningKeyModule | undefined;
let loading: Promise<DeviceSigningKeyModule> | null = null;

/** The module if it has loaded, else undefined. */
export function peekDeviceSigningKey(): DeviceSigningKeyModule | undefined {
  return loaded;
}

/** The module, loading it on first call; one load, shared by every caller. */
export function loadDeviceSigningKey(): Promise<DeviceSigningKeyModule> {
  if (loaded) return Promise.resolve(loaded);
  loading ??= import('./deviceSigningKey')
    .then((module) => (loaded = module))
    .catch((error: unknown) => {
      // A failed load (a deployment replaced under the tab) is tried again by the next caller.
      loading = null;
      throw error;
    });
  return loading;
}
